import { useQuery } from '@tanstack/react-query';
import { fetchWorkspaceAccess, AccessResponse, AccessPermissions } from '@/api/access.api';

/**
 * useAccessResolver Hook
 * 
 * Fetches and provides access information for a workspace.
 * Exposes helper functions for checking module access, permissions, and plan.
 * 
 * @param workspaceId - The workspace ID (optional)
 * @returns Object with access information and helper functions
 */
export function useAccessResolver(workspaceId: string | undefined) {
  // Fetch access information
  const query = useQuery({
    queryKey: ['workspace-access', workspaceId],
    queryFn: async (): Promise<AccessResponse> => {
      if (!workspaceId) {
        // Return safe defaults if no workspace ID
        return {
          modules: [],
          permissions: {},
          plan: {
            code: 'trial',
            name: 'Trial',
            is_trial: true,
          },
        };
      }

      // fetchWorkspaceAccess already handles errors and returns safe defaults
      return await fetchWorkspaceAccess(workspaceId);
    },
    enabled: !!workspaceId, // Only fetch when workspaceId exists
    staleTime: 60 * 1000, // 1 minute
    retry: 0, // Don't retry to avoid error storms
  });

  const data = query.data || {
    modules: [],
    permissions: {},
    plan: {
      code: 'trial',
      name: 'Trial',
      is_trial: true,
    },
  };

  // Helper function to check if a module is enabled
  const hasModule = (moduleKey: string): boolean => {
    const modules = data.modules || [];
    return modules.includes(moduleKey);
  };

  // Helper function to check write permission for a resource
  const canWrite = (resource: string): boolean => {
    const permissions = data.permissions || {};
    const resourcePerms = permissions[resource];
    if (!resourcePerms) return false;
    return resourcePerms.write === true;
  };

  // Helper function to check admin permission for a resource
  const isAdmin = (resource?: string): boolean => {
    const permissions = data.permissions || {};
    if (resource) {
      const resourcePerms = permissions[resource];
      if (!resourcePerms) return false;
      return resourcePerms.admin === true;
    }
    // If no resource specified, check if user has any admin permission
    return Object.values(permissions).some((perm: AccessPermissions) => perm.admin === true);
  };

  return {
    modules: data.modules || [], // Never undefined, always array
    permissions: data.permissions || {}, // Never undefined, always object
    plan: data.plan,
    hasModule,
    canWrite,
    isAdmin,
    isLoading: query.isLoading,
    error: query.error,
  };
}
